import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@bw/shared/hooks";
import { leagueApi } from "@bw/shared/api";
import { queryKeys } from "../../../common/query/queryClient";

type Action = "join" | "cancel";
export function useSessionParticipation(
  sessionIds: string[],
  allowed: boolean,
) {
  const { user } = useAuth();
  const client = useQueryClient();
  const enabled = Boolean(user && allowed && sessionIds.length);
  const query = useQuery({
    queryKey: [
      ...queryKeys.leagues.all,
      "my-session-participation",
      user?.uid,
      sessionIds,
    ],
    queryFn: () => leagueApi.getMySessionParticipation(sessionIds),
    enabled,
  });
  const locks = useRef(new Set<string>());
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [errors, setErrors] = useState<
    Record<string, { action: Action; message: string }>
  >({});
  async function update(sessionId: string, action: Action) {
    const state = query.data?.find((item) => item.session_id === sessionId);
    if (
      !enabled ||
      query.isError ||
      query.isFetching ||
      locks.current.has(sessionId) ||
      !state
    )
      return;
    if (
      action === "join"
        ? !state.can_join
        : !state.can_cancel || !state.session_reg_id
    )
      return;
    locks.current.add(sessionId);
    setPending(new Set(locks.current));
    setErrors((previous) => {
      const next = { ...previous };
      delete next[sessionId];
      return next;
    });
    let failed = false;
    try {
      if (action === "join") await leagueApi.registerForSession(sessionId);
      else await leagueApi.cancelSessionRegistration(state.session_reg_id!);
    } catch {
      failed = true;
    }
    // A conflict/network failure can still mean the source changed. Reconcile
    // from self reads; no mutation receipt becomes a second status store.
    try {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.leagues.all }),
        client.invalidateQueries({ queryKey: queryKeys.me.leagueRegistrations() }),
      ]);
    } finally {
      locks.current.delete(sessionId);
      setPending(new Set(locks.current));
      if (failed)
        setErrors((previous) => ({
          ...previous,
          [sessionId]: {
            action,
            message:
              action === "join"
                ? "Could not join this session. Check its status and try again."
                : "Could not cancel this session. Please try again.",
          },
        }));
    }
  }
  return { query, enabled, pending, errors, update };
}
